/**
 * The query descriptors the app shell and the route prefetch share (BEN-141 QF6).
 *
 * WHY THIS FILE EXISTS. Every signed-in route in the `qa` sweep reported
 * `network.waterfall`: three data requests in a row, none of them waiting on
 * the last one's ANSWER, all of them waiting on the last one's COMPONENT.
 * `OnboardingGate` returns `null` until `/profile/me/` resolves, so nothing
 * below it is mounted and nothing below it can fetch; `AppLayout` then mounts
 * and fires `/payments/status/`, `/banking/accounts/` and `/dashboard/alerts/`;
 * `SubscriptionGate` withholds its `<Outlet/>` until the status answers, so the
 * page's own query starts third. Three rounds of latency to fetch four things
 * that share exactly one dependency between them: the Clerk session.
 *
 * A shared descriptor is what lets the prefetch and the hook be the same query
 * rather than two spellings of one. React Query dedupes on the key,
 * so a prefetch started above the gate and a `useQuery` mounted below it join
 * one in-flight request; a key that drifted by a character would instead double
 * the traffic, which is why the key, the fetcher and the stale window are
 * written here once and spread into the hooks rather than copied.
 *
 * NOT `queryOptions()`. That helper brands the key with the descriptor's exact
 * tuple type, and several of these hooks pass their own generics and a `select`
 * (`useQuery<ApiDebtOverview, Error, DebtOverview>`), which the brand then
 * refuses. A plain descriptor spreads into all three call shapes and still
 * gives the prefetch and the hook one definition, which is the whole point.
 *
 * WHAT IS NOT HERE. `enabled` and `select` stay at the call site: the first is
 * a question about the component (is this user signed in, is this dialog open)
 * and the second is a question about the consumer's shape. A prefetch has no
 * component and no consumer, and `prefetchQuery` ignores both.
 */
import type { QueryFunction, QueryKey } from "@tanstack/react-query";

import { listAccounts } from "@/api/banking";
import { getCategories, getCategoryRules } from "@/api/categories";
import { getDashboardAlerts, getDashboardRemainder } from "@/api/dashboard";
import { getDebtOverview } from "@/api/debts";
import { getFinancialAnalysis } from "@/api/financialAnalysis";
import { getForecastPeriods } from "@/api/forecasting";
import { getForecastPlan } from "@/api/forecastPlan";
import { getGoalsOverview } from "@/api/goals";
import { getInsights } from "@/api/insights";
import { getPaycheck } from "@/api/paycheck";
import { getSubscriptionStatus } from "@/api/payments";
import { getProfile } from "@/api/profile";

/** Two minutes, the app-wide default for data that only a sync or an edit moves. */
const STALE_TIME = 1000 * 60 * 2;

/** The profile is read by the gate on every navigation; five minutes there. */
const PROFILE_STALE_TIME = 1000 * 60 * 5;

/** Subscription state drives a redirect, so it is the one kept tightest. */
const SUBSCRIPTION_STALE_TIME = 60_000;

/** Default page counts, so the prefetch and the hook cannot key differently. */
export const FORECAST_PERIOD_COUNT = 6;
export const PAYCHECK_PERIOD_COUNT = 4;

/** Key, fetcher and stale window: the three things a prefetch and a hook must agree on. */
export interface QueryDescriptor<TData> {
  queryKey: QueryKey;
  queryFn: QueryFunction<TData>;
  staleTime?: number;
}

/** Identity, for the inference. The key stays WIDE (`QueryKey`, not the literal
 *  tuple) on purpose: a descriptor narrowed to `readonly ["debts"]` cannot be
 *  spread into `useQuery<ApiDebtOverview, Error, DebtOverview>`, whose key
 *  parameter defaults to `readonly unknown[]`, and three hooks here need those
 *  generics for their `select`. */
function describe<TData>(descriptor: QueryDescriptor<TData>): QueryDescriptor<TData> {
  return descriptor;
}

export const profileQuery = describe({
  queryKey: ["profile", "me"] as const,
  queryFn: getProfile,
  staleTime: PROFILE_STALE_TIME,
});

export const subscriptionStatusQuery = describe({
  queryKey: ["subscription", "status"] as const,
  queryFn: getSubscriptionStatus,
  staleTime: SUBSCRIPTION_STALE_TIME,
});

export const bankAccountsQuery = describe({
  queryKey: ["bankAccounts"] as const,
  queryFn: listAccounts,
});

export const dashboardAlertsQuery = describe({
  queryKey: ["dashboard", "alerts"] as const,
  queryFn: getDashboardAlerts,
  staleTime: STALE_TIME,
});

export const dashboardRemainderQuery = describe({
  queryKey: ["dashboard", "remainder"] as const,
  queryFn: getDashboardRemainder,
  staleTime: STALE_TIME,
});

export const forecastPlanQuery = describe({
  queryKey: ["forecast-plan"] as const,
  queryFn: getForecastPlan,
  staleTime: STALE_TIME,
});

export const forecastPeriodsQuery = (count: number = FORECAST_PERIOD_COUNT) =>
  describe({
    queryKey: ["forecast-periods", count] as const,
    queryFn: () => getForecastPeriods(count),
    staleTime: STALE_TIME,
  });

export const goalsOverviewQuery = describe({
  queryKey: ["goals"] as const,
  queryFn: getGoalsOverview,
  staleTime: STALE_TIME,
});

export const debtOverviewQuery = describe({
  queryKey: ["debts"] as const,
  queryFn: getDebtOverview,
  staleTime: STALE_TIME,
});

export const insightsQuery = describe({
  queryKey: ["insights"] as const,
  queryFn: getInsights,
  staleTime: STALE_TIME,
});

export const financialAnalysisQuery = describe({
  queryKey: ["financial-analysis"] as const,
  queryFn: getFinancialAnalysis,
  staleTime: STALE_TIME,
});

export const paycheckQuery = (count: number = PAYCHECK_PERIOD_COUNT) =>
  describe({
    queryKey: ["paycheck", count] as const,
    queryFn: () => getPaycheck(count),
    staleTime: STALE_TIME,
  });

export const categoriesQuery = describe({
  queryKey: ["categories"] as const,
  queryFn: getCategories,
});

export const categoryRulesQuery = describe({
  queryKey: ["category-rules"] as const,
  queryFn: getCategoryRules,
});
